'use client';

import { useEffect, useRef } from "react";
import HeroSection from "@/sections/HeroSection";
import SignUpSection from "@/sections/SignUpSection";
import ReferralSection from "@/sections/ReferralSection";
import QRSection from "@/sections/QRSection";
import SlideImageSection from "@/sections/SlideImageSection";
import TestimonialCard from "@/components/ui/TestimonialCard/TestimonialCard";
import { BrandInfoSectionContent, CTASectionContent, FacebookReviewSectionContent, GeneralTestimonialSectionContent, GoogleReviewSectionContent, LanguageType, NavbarSectionContent, TeamMemberSectionContent } from "@/lib/types";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import NavbarContainer from "@/components/client/NavbarContainer";
import FooterWrapper from "@/components/ui/FooterWrapper";
import { PageData } from "@/lib/types";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import WaitlistSection from "@/sections/WaitlistSection";
import FoundingBenefitsSection from "@/sections/FoundingBenefitsSection";
import TimelineSection from "@/sections/TimelineSection";
import {
  Gift,
  Sun,
  Sparkle,
  Medal,
  Users
} from "@phosphor-icons/react";
import { Percent } from "@phosphor-icons/react/dist/ssr";
import useAspectRatio from "@/hooks/useAspectRatio";
import { useLoading } from "@/contexts/LoadingContext";
import { addToCart } from "@/lib/gtm";

import { debug } from '@/lib/debug';
import { useIsTb } from '@/contexts/BreakpointContext';
import { setLanguage } from '@/lib/language';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface SignupPageClientProps {
  pageData: PageData | null;
  initialLanguage: LanguageType;
}

// TODO: colors here are not correct.

export default function SignupPageClient({ pageData, initialLanguage }: SignupPageClientProps) {
  const language: LanguageType = initialLanguage;
  const isTb = useIsTb();
  // const textBenefitsSectionRef = useRef<HTMLDivElement | null>(null);
  const slideImagePositionRef = useRef<number | null>(null);
  const referralPositionRef = useRef<number | null>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const animatedNavigate = useAnimatedNavigate();
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();



  useEffect(() => {
    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
    addToCart();

    // navigate('/signup', { state: {} });
  }, []);

  useGSAP(() => {
    // Set initial background color
    document.body.style.backgroundColor = '#FFF6E6';
    document.body.classList.add('will-change-bg');
  }, { dependencies: [] });


  const { contextSafe } = useGSAP({});

  // Create a safe animation function that can be called anytime
  const animateBg = contextSafe(() => {

    // Kill only the color transition ScrollTriggers
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.vars.id?.toString().startsWith('color-transition-')) {
        trigger.kill();
      }
    });

    // Add color transition sections
    const sections: {
      startY: number;
      fromColor: string;
      toColor: string;
    }[] = [];
    if (slideImagePositionRef.current && referralPositionRef.current) {
      sections.push({
        startY: slideImagePositionRef.current,
        fromColor: '#FFF6E6', // TODO: use variables
        toColor: '#F7E8D7',
      });
      sections.push({
        startY: referralPositionRef.current,
        fromColor: '#F7E8D7', // TODO: use variables
        toColor: '#FFF6E6', // TODO: use variables
      });
    }

    sections.forEach((section, index) => {
      ScrollTrigger.create({
        start: `top+=${section.startY} center`,
        // markers: true,
        id: `color-transition-${index}`,
        onEnter: () => {
          gsap.to(document.body, {
            backgroundColor: section.toColor,
            duration: 1,
            ease: "power2.inOut"
          });
        },
        onLeaveBack: () => {
          gsap.to(document.body, {
            backgroundColor: section.fromColor,
            duration: 1,
            ease: "power2.inOut"
          });
        }
      });
    });
  });


  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    debug.log('[SignupPageClient] aspectRatio changed:', aspectRatio);
    if (aspectRatio !== 0) {
      debug.log('[SignupPageClient] aspectRatio ready, setting AspectRatioReady to true');
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);


  if (!pageData) {
    debug.log('--------------------------------');
    debug.log('PAGE DATA IS NULL');
    debug.log('--------------------------------');
    return null; // TODO: Or a loading spinner
  }


  const ctaSectionPageData: CTASectionContent = pageData.sections.find(section => section.type === 'call-to-action')?.content as CTASectionContent;
  const navbarSectionPageData: NavbarSectionContent = pageData.sections.find(section => section.type === 'navbar')?.content as NavbarSectionContent;
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;
  const generalTestimonialSectionPageData: GeneralTestimonialSectionContent = { testimonials: pageData.sections.find(section => section.type === 'general-testimonial')?.content } as GeneralTestimonialSectionContent;
  const googleReviewSectionPageData: GoogleReviewSectionContent = pageData.sections.find(section => section.type === 'google-review')?.content as GoogleReviewSectionContent;
  const facebookReviewSectionPageData: FacebookReviewSectionContent = { facebookReviews: pageData.sections.find(section => section.type === 'facebook-review')?.content } as FacebookReviewSectionContent;

  // Beauty Therapists for the Referral form's "Recommended expert" dropdown.
  // Strapi returns the locale-appropriate position string (English vs Arabic),
  // so we accept both variants — keeps the filter working regardless of which
  // locale the page is rendered in.
  const teamMemberSectionPageData: TeamMemberSectionContent = {
    teamMembers: pageData.sections.find(section => section.type === 'team-member')?.content
  } as TeamMemberSectionContent;
  const BEAUTY_THERAPIST_POSITIONS = ['beauty therapist', 'معالجة تجميل'];
  const therapistNames: string[] = (teamMemberSectionPageData.teamMembers ?? [])
    .filter((m) => BEAUTY_THERAPIST_POSITIONS.includes(m.teamMemberPosition?.toLowerCase().trim()))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((m) => m.teamMemberName);



  return (
    <div className=" flex flex-col section-gaps relative">

      {/* <HeroSection
        title="Sign up for Rivive"
        // imageSrc="./hero-image.webp"
        imageSrc="./hero-image.avif"
        imageAlt="Mountain landscape"
        language={language}
        titleFont="big"
      /> */}

      <HeroSection
        title={language === 'arabic' ? "انضموا إلى ريڤيڤ" : "Join Rivive's founding circle"}
        paragraph={language === 'arabic' ? "قم بالتسجيل لدينا في قائمة الانتظار لتبدأ رحلتك معنا, أو قم بالتقدم بطلب للحصول على عضوية المؤسسين لفتح مجموعة من المكافآت الحصرية لولائك معنا مدى الحياة" : "Sign up for our waitlist to begin your journey, or apply for a Founding Membership to unlock a lifetime of exclusive rewards for your early dedication."}
        hint={language === 'arabic' ? "*تتوفر عروض حصرية لمن يسجل مبكرا من عملائنا" : "*Exclusive early-member benefits available"}
        buttonText={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
        // imageSrc="https://images.unsplash.com/photo-1717330550630-659d5d3f5f29?w=2400&h=1041"
        // imageSrc="./hero-image.avif"
        imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/Sign_Up_Hero_c22f7e352f.webp"
        imageAlt="Mountain landscape"
        // mediaType="video"
        // videoSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/sample_video_dba0075496.mp4"
        language={language}
        titleFont="small"
      />

      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        promoBarText={navbarSectionPageData?.promoBarText}
        promoBarVisibility={navbarSectionPageData?.promoBarVisibility}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
        brandInfo={brandInfoSectionPageData}
      />

      <div
        ref={shadowRef}
        className="absolute w-full aspect-3/4 tb:aspect-3/2 z-0 opacity-[8%] tb:opacity-10 pointer-events-none"
        style={{
          top: `0px`,
          transform: isTb ? 'translateY(-50%)' : '',
          backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'darken'
        }}
      />

      <WaitlistSection
        text={language === 'arabic' ? "لضمان رعاية استثنائية, تحتفظ ريڤيڤ بالوصول الحصري لقائمة الانتظار الخاصة بالعملاء" : "To ensure exceptional care, Rivive maintains exclusive access via a private waitlist"}
        showLogo={true}
        language={language}
        subheading={language === 'arabic' ? "هناك طريقتين للانضمام معنا" : "TWO WAYS TO JOIN US"}
        column1Heading={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
        column1Description={language === 'arabic' ? "كن من أوائل من يجربون ريڤيڤ من خلال قائمة انتظارنا الخاصة. لا توجد متطلبات محددة، فقط فرصة مبكرة للاستمتاع برعاية شخصية غير جراحية بمجرد اكتمال العدد في القائمة." : "Be among the first to experience Rivive through our private waitlist. No special requirements, just an early chance to enjoy personalized, non-invasive care once capacity allows."}
        column1Benefits={language === 'arabic' ? [
          {
            title: '',
            content: 'كن من بين الاوائل لمن يجربون رعاية ريڤيڤ'
          },
          {
            title: '',
            content: 'احجز مكانك في مرحلة التأسيس'
          },
          {
            title: '',
            content: 'عيش التجربة مع ريڤيڤ قبل الإطلاق الرسمي'
          }
        ] : [
          {
            title: '',
            content: "Be among the first to experience Rivive's care"
          },
          {
            title: '',
            content: 'Secure your place in our founding chapter'
          },
          {
            title: '',
            content: 'Experience Rivive before public launch'
          }
        ]}
        column2Heading={language === 'arabic' ? "كن أحد الأعضاء المؤسسين" : "Become a Founding Member"}
        column2Description={language === 'arabic' ? "مع 50 مكاناً متاحاً فقط, ستصبح عضويتك التأسيسية جزءاً من قصة تأسيس ريڤيڤ و ستمنح لك امتيازات لن تعرض مجدداً. للانضمام يتطلب الاشتراك في باقة البريميوم / VIP." : "With only 50 available slots, a Founding Membership will mark you as part of Rivive's founding story and unlock privileges never to be offered again. Enrollment requires a Premium/VIP plan."}
        column2Benefits={language === 'arabic' ? [
          {
            title: '',
            content: 'استمتع بقائمة الاسعار الحصرية بالأعضاء المؤسسين'
          },
          {
            title: '',
            content: 'فتح مجال الأولوية في الوصول إلى ابتكاراتنا'
          },
          {
            title: '',
            content: 'كن جزءاً من دائرة الأعضاء المؤسسين الحصرية'
          }
        ] : [
          {
            title: '',
            content: 'Enjoy founding-member-only pricing'
          },
          {
            title: '',
            content: 'Unlock priority access to our innovations'
          },
          {
            title: '',
            content: 'Become part of the exclusive Founding Members circle'
          }
        ]}
        benefits={[]}
      />

      <SignUpSection
        id="signup-section"
        language={language}
      />


      <FoundingBenefitsSection
        headlineText={language === 'arabic' ? "امتيازات مدى الحياة مخصصة فقط لأعضائنا المؤسسين" : "Lifetime privileges reserved for our Founding Members"}
        subheadingText={language === 'arabic' ? "عروض حصرية" : "EXCLUSIVE REWARDS"}
        paragraphText={language === 'arabic' ? "تم إنشاء العضويات التأسيسية خصيصاً للمؤمنين الأوائل في رؤية ريڤيڤ, وهي طريقتنا لمكافأة ثقتك بفوائد حصرية ودائمة لن يتم تقديمها مرة أخرى ابداً." : "Created for early believers in Rivive's vision, Founding Memberships are our way of rewarding your trust with exclusive, lasting benefits that will never be offered again."}
        language={language}
        benefits={[
          {
            heading: language === 'arabic' ? 'هدية ترحيب حصرية' : 'Exclusive welcome gift',
            text: language === 'arabic'
              ? 'استمتعوا بتقييم مجاني مميز, يشمل التوجيه من الاخصائية وتحليل الخبراء, والبروتوكولات وضعت خصيصاً لكم.'
              : 'Enjoy a free Signature Evaluation, including specialist guidance, expert analysis, and tailored protocols.',
            icon: Gift
          },
          {
            heading: language === 'arabic' ? 'أسعار الأعضاء المؤسسين' : 'Founding Member pricing',
            text: language === 'arabic' ? 'احصلوا على خصم 20% على باقة العلاج الأولى الخاصة بكم مع عرضا المخصص للأعضاء المؤسسين لمرة واحدة.' : 'Save 20% on your first treatment package with our one-time offer exclusive to Founding Members.',
            icon: Percent
          },
          {
            heading: language === 'arabic' ? 'أولوية الوصول لمدى الحياة' : 'Lifetime priority access',
            text: language === 'arabic' ? 'احصلوا على أولوية الوصول الى جميع المناسبات والخدمات والمبادرات والعلاجات الجديدة التي سيتم تقديمها.' : 'Gain priority access to all new events, services, innovations, and treatments that we will introduce.',
            icon: Sun
          },
          {
            heading: language === 'arabic' ? 'تجارب التجميل الحصرية' : 'Exclusive beauty experiences',
            text: language === 'arabic' ? 'احصلوا على الأولوية في الدعوات للمناسبات الخاصة, من العروض التقديمية لتجارب التجميل.' : 'Receive priority invitations to private events, from showcases to beauty experiences.',
            icon: Sparkle
          },
          {
            heading: language === 'arabic' ? 'مكافآت الولاء المتقدمة' : 'Enhanced loyalty rewards',
            text: language === 'arabic' ? 'كن أول من يحصل على نقاط ولاء إضافية عند إطلاق برنامجنا، لتتمكن من الاستفادة من المزايا الحصرية والخدمات الخاصة.' : 'Be first to earn bonus loyalty points when our program launches, unlocking premium perks and special services.',
            icon: Medal
          },
          {
            heading: language === 'arabic' ? 'إمكانية الإحالة ذات الأولوية' : 'Priority referral access',
            text: language === 'arabic' ? 'وفروا إمكانية الحجز ذات الأولوية لإحالاتك لتجاوز قوائم الانتظار.' : 'Provide priority booking access for your referrals to skip waiting lists.',
            icon: Users
          },
        ]}
      />

      <TimelineSection
        subheadingText={language === 'arabic' ? "بداية رحلتك" : "YOUR BEGINNING"}
        headlineText={language === 'arabic' ? "رحلة تأخذك من لحظة التسجيل إلى جمال أبدي" : "A journey that will take you from sign-up to timeless beauty"}
        language={language}
        timelineItems={[
          {
            number: '1',
            title: language === 'arabic' ? 'انضم الى قائمة الانتظار التي تعكس أهدافك للجمال' : 'Join the waitlist that reflects your beauty goals',
            text: language === 'arabic' ? 'منذ البداية، الخيار لك. يمكنك الانضمام إلى قائمة الانتظار لدينا لحجز مقعدك دون أي متطلبات إضافية، أو الانضمام إلى عضوية التأسيس لمزايا تمتد مدى الحياة.' : 'Right from the beginning, the choice is yours. You can either join our waitlist to secure your spot with no additional requirements, or step into a Founding Membership for a lifetime of privileges.',
            additionalText: language === 'arabic' ? '*تتطلب العضويات التأسيسية اشتراكًا في باقة البريميوم/VIP.' : '*Founding Memberships require a Premium/VIP plan.',
            imageUrl: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/Founding_Timeline_Waitlist_21a1fe9731.webp',
            imageAlt: language === 'arabic' ? 'الخطوة 1' : 'Step 1'
          },
          {
            number: '2',
            title: language === 'arabic' ? 'سوف نتواصل معك عندما تصل لحظتك' : 'We will connect with you when your moment arrives',
            text: language === 'arabic' ? 'بمجرد وصول دورك، توقع مكالمة مخصصة لك لمناقشة احتياجاتك وتأمين زيارتك لمركزنا.' : 'As soon as your turn arrives, expect a dedicated call to discuss your needs and secure your visit to our center.',
            imageUrl: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/Founding_Timeline_Connect_53fd27c752.webp',
            imageAlt: language === 'arabic' ? 'الخطوة 2' : 'Step 2'
          },
          {
            number: '3',
            title: language === 'arabic' ? 'يوم زيارتك التقييمية المميزه' : 'The day of your signature evaluation visit',
            text: language === 'arabic' ? 'لقد حان اليوم المنتظر! عند دخولكِ إلى ريڤيڤ، ستبدأ تجربة جديدة: مساحة هادئة، ترحيب دافئ، وتقييم شامل مصمم لوضع خطة جمالكِ الشخصية.' : 'The big day has arrived! As you enter Rivive, the experience unfolds: a calming space, a warm welcome, and an in-depth evaluation designed to craft your personalized beauty plan.',
            imageUrl: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/Founding_Timeline_Visit_cbeec3c70f.webp',
            imageAlt: language === 'arabic' ? 'الخطوة 3' : 'Step 3'
          },
          {
            number: '4',
            title: language === 'arabic' ? 'قم باختيار خطتك ودع التحول الخاص بك يبدأ' : 'Select a plan and let your transformation begin',
            text: language === 'arabic' ? 'بناءً على تقييمك، سنصمم بروتوكولات علاجية مخصصة تُلبي احتياجاتك الخاصة. سيضمن توجيهنا التعاوني لك وضع الخطة المثالية لتحولك.' : 'Based on your evaluation, we will design personalized treatment protocols that address your specific needs. Our collaborative guidance will next ensure that you embark on the perfect plan for your transformation.',
            additionalText: language === 'arabic' ? '*تتطلب العضويات التأسيسية اشتراكًا في باقة البريميوم/VIP.' : '*Founding Memberships require a Premium/VIP plan.',
            imageUrl: 'https://dependable-apparel-f6b4f02674.media.strapiapp.com/Founding_Timeline_Plan_cce3d6c5f6.webp',
            imageAlt: language === 'arabic' ? 'الخطوة 4' : 'Step 4'
          },
        ]}
        buttonText={language === 'arabic' ? "انضم لقائمة الانتظار" : "Join our waitlist"}
        testimonialText={language === 'arabic' ? "إنه واحد من أفضل المراكز التي زرتها. حقًا بمستوى عالمي!" : "It's one of the best centers I've visited. Truly world-class!"}
        testimonialAuthor={language === 'arabic' ? "سلمى أ." : "Salma A."}
      />

      <TestimonialCard
        testimonials={generalTestimonialSectionPageData.testimonials.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))}
        addTopPadding={true}
        language={language}
      />

      <SlideImageSection
        text={language === 'arabic' ? "انغمس في واحتنا المستوحاة من الطبيعة، حيث يشرق جمالك الحقيقي ويبدأ مشوارك نحو التميز." : "Step into our nature-inspired oasis, where your journey to authentic beauty awaits."}
        imageSrc="https://dependable-apparel-f6b4f02674.media.strapiapp.com/Index_Center_a521018ca9.webp"
        imageAlt={language === 'arabic' ? "منظر طبيعي" : "Mountain landscape"}
        language={language}
        onInitialPosition={(pos) => {
          slideImagePositionRef.current = pos;
          animateBg();
        }}
        aspectRatio={aspectRatio}
      />

      <ReferralSection
        language={language}
        recommendedExperts={therapistNames}
        subheading={language === 'arabic' ? "شاركوا الإشراقة" : "SHARE THE GLOW"}
        heading={language === 'arabic' ? "أهدوا رحلة ريڤيڤ" : "Gift the Rivive journey"}
        paragraph={language === 'arabic' ? "قوموا بدعوة شخصٍ مميزًا لتجربة علاجاتنا التحويلية. ستنتظركم مزايا حصرية بمجرد انضمام المدعو إلى عضويتنا." : "Invite someone special to experience our transformative treatments. Exclusive benefits await you both as soon as your invitee becomes a member."}
        subheading2={language === 'arabic' ? "ما ستكسبونه" : "What you will earn"}
        paragraphList2={
          language === 'arabic' ? [
            "جلسة مجانية مميزة كعربون شكر منا",
            "تمت إضافة جلسة علاج مجانية إلى الباقة التي اخترتها",
            "ترقية مستوى العضوية لفتح مزايا مستقبلية ومكافآت حصرية لأعضائنا"
          ] : [
            "A complimentary treatment session added to your chosen package",
            "Loyalty points awarded when our Circle of Radiance program launches",
            "Enhanced membership tier status unlocking future privileges and exclusive community rewards"
          ]
        }
        subheading3={language === 'arabic' ? "ما سيستمتعون به" : "What they will enjoy"}
        paragraphList3={
          language === 'arabic' ? [
            "أولوية الوصول إلى الحجوزات لتجاوز قوائم الانتظار",
            "تمت إضافة جلسة علاج مجانية إلى الباقة التي اخترتها",
            "جلسة تقييم مميزة مجانية تشمل فحصًا متخصصًا وإنشاء خطة علاجية شخصية مصممة خصيصًا لحالتك."
          ] : [
            "Priority booking access to skip waiting lists",
            "A complimentary treatment session added to your chosen package",
            "Complimentary Signature Evaluation session with specialist assessment and personalized protocol creation"
          ]
        }
        onInitialPosition={(pos) => {
          referralPositionRef.current = pos;
          animateBg();
        }}
      />


      <QRSection googleReviewSectionPageData={googleReviewSectionPageData} facebookReviewSectionPageData={facebookReviewSectionPageData} language={language} />

      <FooterWrapper
        ctaSectionPageData={ctaSectionPageData}
        brandInfoSectionPageData={brandInfoSectionPageData}
        language={language}
        setLanguage={setLanguage}
      />
    </div>
  )
}
